"use client"

import {
  createContext,
  createElement,
  Fragment,
  useContext,
  type ReactNode,
} from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  BASELINE_DATA,
  CLOSE_DATA,
  COMPARE_DATA,
  DOCTORS_DATA,
  FAMILIAR_DATA,
  FULL_VIEW_DATA,
  ONE_PLAN_DATA,
  PLAN_TILES_DATA,
  PRACTICAL_DATA,
  STORIES_DATA,
  WHAT_YOU_GET_DATA,
  WHY_NOW_DATA,
} from "./home-v4-data"
import { formatCheckupCount } from "@/lib/review-stats"
import { useCountry } from "@/components/layout/country-provider"
import { isBookingOpen } from "@/i18n/config"

/**
 * The page's copy, read from the `homeV4` namespace and handed back in the
 * shapes the sections were written against while the copy was still plain
 * constants (`HERO`, `WHY_NOW`, …). Anything that is not copy — sources,
 * tile geometry, logos, icons, counts, a proper noun — comes from
 * `home-v4-data.ts` and is merged in here, so a section keeps one object
 * per concern and the catalogue holds only what gets translated.
 *
 * The English is the copy ported from the Lucis-informed draft
 * (`research/lucis/prototype/home-copy-draft.html`, Eugene's section list
 * of 18.09). `de` and `fr` are first translations of it, to be reworked once
 * the English settles; the other mirror locales skip this namespace
 * (`scripts/lint-mirror-locales.ts`) until the page is localised per market,
 * because the copy is Swiss through and through.
 */
/**
 * Lets another page reword some of these strings for its own reader: /gift
 * speaks about the person receiving the gift ("their plan") where the
 * homepage speaks to the reader ("your plan"). The override is asked first
 * for each `homeV4` key, with that key's values, and a key it does not
 * answer (`undefined`) keeps the homepage's string. Without a provider,
 * which is every page but /gift, nothing changes.
 */
export type HomeV4CopyOverride = (
  key: string,
  values?: Record<string, string | number>
) => string | undefined

const HomeV4CopyOverrideContext = createContext<HomeV4CopyOverride | null>(null)
export const HomeV4CopyOverrideProvider = HomeV4CopyOverrideContext.Provider

export function useHomeV4Copy() {
  const homeT = useTranslations("homeV4")
  const override = useContext(HomeV4CopyOverrideContext)
  const t = (
    override
      ? (
          key: Parameters<typeof homeT>[0],
          values?: Record<string, string | number>
        ) =>
          override(key, values) ??
          homeT(key, values as Parameters<typeof homeT>[1])
      : homeT
  ) as typeof homeT
  const { code: country } = useCountry()
  const doctorCall = t("doctorCall")
  /** Leaves out an item about the MRI where the MRI does not book yet. */
  const showScans = isBookingOpen(country, "mri")
  const withScans = (item: object) =>
    showScans || !("scan" in item && item.scan)

  /** "/year" and its translations; the figure itself is `membershipAnnualAmount`. */
  const membershipPeriod = t("membershipPeriod")

  const HERO = {
    /** "{cities} cities in {countries} countries" — the figures are counted
        live from the CMS's published clinics in `home-v4-page-data.ts`. */
    eyebrow: (cities: number, countries: number) =>
      // The catalogue's plain `{cities}` argument is typed as a string.
      t("heroEyebrow", {
        cities: String(cities),
        countries: String(countries),
      }),
    heading: {
      plain: `${t("heroHeadingPlain")} `,
      accent: t("heroHeadingAccent"),
    },
    body: t("heroBody"),
    rating: t("heroRatingLabel", {
      checkups: formatCheckupCount(country),
    }),
    /** "Start from {price}/year"; the price is the plan's live yearly
        figure (`membershipAnnualAmount`), formatted by the caller. The
        closing "/year" is set a size smaller than the label (Sven, 08.10). */
    ctaPrimary: (price: string): ReactNode => {
      const label = t("heroCtaPrimary", { price })
      if (!label.endsWith(membershipPeriod)) return label
      return createElement(
        Fragment,
        null,
        label.slice(0, -membershipPeriod.length),
        createElement("span", { className: "type-body-md" }, membershipPeriod)
      )
    },
    ctaSecondary: t("heroCtaSecondary"),
    imageAlt: t("heroImageAlt"),
  }

  const TRUST = [t("trust1"), t("trust2"), t("trust3")] as const

  const WHY_NOW = {
    heading: t("whyNowHeading"),
    triggers: [
      t("whyNowTrigger1"),
      t("whyNowTrigger2"),
      t("whyNowTrigger3"),
    ] as const,
    quote: t("whyNowQuote"),
    author: WHY_NOW_DATA.author,
    role: t("whyNowRole"),
  }

  /** 03 — "Sound familiar?": the worry in the reader's words, then what the
      check-up does about it. Sits on the warm band that used to carry the
      statistics cards. No source links (Eugene, 18.09). */
  const FAMILIAR = {
    eyebrow: t("familiarEyebrow"),
    heading: t("familiarHeading"),
    rows: FAMILIAR_DATA.rows
      .map((row, i) => ({
        ...row,
        title: t(`familiar${i + 1}Title` as "familiar1Title"),
        body: t(`familiar${i + 1}Body` as "familiar1Body"),
      }))
      .filter(withScans),
    pause: t("familiarPause"),
    play: t("familiarPlay"),
    slide: (n: number, total: number) =>
      t("familiarSlide", { n: String(n), total: String(total) }),
  }

  const FULL_VIEW = {
    eyebrow: t("fullViewEyebrow"),
    heading: {
      plain: t("fullViewHeadingPlain"),
      accent: t("fullViewHeadingAccent"),
    },
    lede: t("fullViewLede"),
    columns: FULL_VIEW_DATA.columns
      .map((col, i) => ({
        ...col,
        title: t(`fullViewCol${i + 1}Title` as "fullViewCol1Title"),
        body: t(`fullViewCol${i + 1}Body` as "fullViewCol1Body"),
      }))
      .filter(withScans),
    closing: t("fullViewClosing"),
    cta: t("fullViewCta"),
    bookCta: t("fullViewBookCta"),
  }

  /** 03b — Compared: Ahead against a standard check-up. Ahead's cell is the
   *  count in row 1, words where a row has `ahead`, a check elsewhere. */
  const COMPARE = {
    eyebrow: t("compareEyebrow"),
    heading: {
      plain: `${t("compareHeadingPlain")} `,
      accent: t("compareHeadingAccent"),
    },
    columns: { standard: t("compareColStandard"), ahead: t("compareColAhead") },
    rows: COMPARE_DATA.rows
      .map((row, i) => ({
        scan: "scan" in row && row.scan,
        kind: row.kind,
        label: t(`compareRow${i + 1}Label` as "compareRow1Label"),
        detail: row.detail
          ? t(`compareRow${i + 1}Detail` as "compareRow2Detail")
          : null,
        /** Words in Ahead's cell instead of the check, e.g. "12 organ systems". */
        ahead:
          "ahead" in row && row.ahead
            ? t(`compareRow${i + 1}Ahead` as "compareRow5Ahead")
            : null,
        standard:
          row.kind === "text"
            ? t(`compareRow${i + 1}Standard` as "compareRow2Standard")
            : "",
      }))
      .filter(withScans),
    count: { ahead: t("compareRow1Ahead"), standard: t("compareRow1Standard") },
    included: t("compareIncluded"),
    notIncluded: t("compareNotIncluded"),
  }

  const ONE_PLAN = {
    eyebrow: t("onePlanEyebrow"),
    heading: {
      plain: `${t("onePlanHeadingPlain")} `,
      accent: t("onePlanHeadingAccent"),
    },
    lede: t("onePlanLede"),
    tiles: ONE_PLAN_DATA.tiles,
    trust: ONE_PLAN_DATA.trust.map((item, i) => ({
      icon: item.icon,
      text: t(`onePlanTrust${i + 1}` as "onePlanTrust1"),
    })),
  }

  /** 05b — What you get: its own immersive section (Eugene, 18.09: "a
      full duplicate, with the new content"), four tiles with the draft's
      plan · trend · findings · what to recheck and when. The demo member
      is Thomas, 46, fictional, as on the phone in 05. */
  const WHAT_YOU_GET = {
    eyebrow: t("onePlanBEyebrow"),
    heading: {
      plain: `${t("onePlanBHeadingPlain")} `,
      accent: t("onePlanBHeadingAccent"),
    },
    lede: t("onePlanBLede"),
    tiles: WHAT_YOU_GET_DATA.tiles,
    trust: ONE_PLAN.trust,
    plan: {
      label: t("onePlanBPlanLabel"),
      items: [t("onePlanBPlan1"), t("onePlanBPlan2"), t("onePlanBPlan3")],
    },
    trend: {
      label: t("onePlanBTrendLabel"),
      value: t("onePlanBTrendValue"),
      body: t("onePlanBTrendBody"),
      ...WHAT_YOU_GET_DATA.trend,
    },
    findings: {
      label: t("onePlanBFindingsLabel"),
      addon: t("onePlanBFindingsAddon"),
      body: t("onePlanBFindingsBody"),
      counts: [
        {
          n: WHAT_YOU_GET_DATA.findings.critical,
          label: t("onePlanBFindingsCritical"),
        },
        {
          n: WHAT_YOU_GET_DATA.findings.minor,
          label: t("onePlanBFindingsMinor"),
        },
        {
          n: WHAT_YOU_GET_DATA.findings.observations,
          label: t("onePlanBFindingsObservations"),
        },
      ],
    },
    recheck: {
      label: t("onePlanBRecheckLabel"),
      rows: [
        { what: t("onePlanBRecheck1What"), when: t("onePlanBRecheck1When") },
        { what: t("onePlanBRecheck2What"), when: t("onePlanBRecheck2When") },
      ],
    },
  }

  const PLAN_TILES = {
    hero: { doctorName: PLAN_TILES_DATA.hero.doctorName },
    phone: {
      ...PLAN_TILES_DATA.phone,
      bioAge: { short: t("planBioAgeShort"), desc: t("planBioAgeDesc") },
    },
  }

  const DOCTORS = {
    /** "Doctors and medical advisory board", not "Who writes your report":
        the grid is not only the people who write it (Ferdinand, 15.10,
        AHD-14161). */
    eyebrow: t("doctorsEyebrow"),
    heading: t("doctorsHeading"),
    /** Anna's line under the heading: a statement in the team's voice, not
        a quotation (Eugene, 18.09), so no quotation marks round it. */
    lead: {
      statement: t("doctorsLeadStatement"),
      photo: DOCTORS_DATA.lead.photo,
    },
    /** Two-line captions keyed by surname; see `home-v4-data.ts`. */
    captions: Object.fromEntries(
      DOCTORS_DATA.captionSurnames.map((surname) => [
        surname,
        t(`doctorsCaption${surname}` as "doctorsCaptionErat"),
      ])
    ) as Record<(typeof DOCTORS_DATA.captionSurnames)[number], string>,
    logos: DOCTORS_DATA.logos,
  }

  const BASELINE = {
    heading: t("baselineHeading"),
    /** The card's line, the same as /pricing's: the check-up leads and
        membership only shows as member prices (Sven, Michèle, 15.10). */
    lede: t("baselineLede"),
    /** "Your first check-up, the full-body MRI, and your doctor's plan…" —
        the component puts the commas and spaces round the MRI insert. */
    ledeMri: {
      before: t("baselineLedeBefore"),
      after: t("baselineLedeAfter"),
    },
    // One label for booking across the site, "Book check-up" (Eugene, 08.10).
    cta: t("baselineCta"),
    doctorCall,
    promises: BASELINE_DATA.promises.map((p, i) => ({
      icon: p.icon,
      text: t(`baselinePromise${i + 1}` as "baselinePromise1"),
    })),
    includedMri: {
      text: t("baselineIncludedMri"),
      highlight: BASELINE_DATA.includedMri.highlight,
    },
    included: BASELINE_DATA.included.map((item, i) => ({
      text: t(`baselineIncluded${i + 1}` as "baselineIncluded1"),
      highlight: item.highlight,
    })),
    upsell: {
      title: t("baselineUpsellTitle"),
      sub: t("baselineUpsellSub"),
      link: t("baselineUpsellLink"),
      priceSuffix: t("baselineUpsellPriceSuffix"),
      ledeInsert: t("baselineUpsellLedeInsert"),
      recurringNote: (price: string, period: string) =>
        t("baselineRecurringNote", { price, period }),
    },
  }

  const INSURANCE = {
    eyebrow: t("insuranceEyebrow"),
    heading: t("insuranceHeading"),
    body: t("insuranceBody"),
    cta: t("insuranceCta"),
  }

  const PRACTICAL = {
    eyebrow: t("practicalEyebrow"),
    heading: {
      plain: t("practicalHeadingPlain"),
      accent: t("practicalHeadingAccent"),
    },
    cta: t("practicalCta"),
    steps: PRACTICAL_DATA.steps.map((step, i) => ({
      label: t(`practicalStep${i + 1}Label` as "practicalStep1Label"),
      figure: t(`practicalStep${i + 1}Figure` as "practicalStep1Figure"),
      body: t(`practicalStep${i + 1}Body` as "practicalStep1Body"),
      optional: step.optional,
    })),
  }

  const REVIEWS = {
    eyebrow: t("reviewsEyebrow"),
    heading: t("reviewsHeading"),
    cta: t("reviewsCta"),
    followersSuffix: t("reviewsFollowersSuffix"),
  }

  /** 10b — Stories. Fictional placeholders until real, consented cases
      exist; every card says so. Set like a text review: the opening line,
      the start of the story, the person. The rest is on its own page. */
  const STORIES = {
    eyebrow: t("storiesEyebrow"),
    heading: t("storiesHeading"),
    fictional: t("storiesFictional"),
    stories: STORIES_DATA.stories.map((story, i) => ({
      id: story.id,
      name: t(`story${i + 1}Name` as "story1Name"),
      headline: t(`story${i + 1}Headline` as "story1Headline"),
      body: t(`story${i + 1}Body` as "story1Body"),
    })),
  }

  const CLOSE = {
    heading: {
      plain: `${t("closeHeadingPlain")} `,
      accent: t("closeHeadingAccent"),
    },
    lede: t("closeLede"),
    features: CLOSE_DATA.features.map((f) => ({
      icon: f.icon,
      text: t(`closeFeature${f.n}`),
    })),
    ctaPrimary: t("closeCtaPrimary"),
    ctaSecondary: t("closeCtaSecondary"),
  }

  return {
    HERO,
    TRUST,
    WHY_NOW,
    FAMILIAR,
    FULL_VIEW,
    COMPARE,
    ONE_PLAN,
    WHAT_YOU_GET,
    PLAN_TILES,
    DOCTORS,
    BASELINE,
    INSURANCE,
    PRACTICAL,
    REVIEWS,
    STORIES,
    CLOSE,
    membershipPeriod,
  }
}
